
import { mapState, mapGetters } from 'vuex';
import AppModalHeader from '@/components/core/AppModalHeader';
import SizeRecommendationInputView from './components/SizeRecommendationInputView';
import SizeRecommendationLoadingView from './components/SizeRecommendationLoadingView';
import SizeRecommendationResultView from './components/SizeRecommendationResultView';
import SizeRecommendationStep from '@/enums/SizeRecommendationStep';

const MINIMUM_LOADING_DURATION = 2000;

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

export default {
  i18nComponentKey: 'WidgetAddOnSizeRecommendationPopup.WidgetAddOnSizeRecommendationPopup',
  name: 'WidgetAddOnSizeRecommendationPopup',
  components: {
    AppModalHeader,
    SizeRecommendationInputView,
    SizeRecommendationLoadingView,
    SizeRecommendationResultView
  },
  data() {
    return {
      step: SizeRecommendationStep.INPUT,
      recommendedSizes: null,
      sizeOptionStats: [],
      sizeFeedbacks: [],
      sizeFeedbacksPagy: null,
      sizeFeedbackFilters: null,
      selectedSizeFeedbackFilters: {},
      isLoadingSizeFeedbacks: false,
      height: null,
      weight: null,
      preferredFitType: null,
      backCount: 0
    };
  },
  computed: {
    ...mapState(['productCode', 'api']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('window', ['windowService']),
    ...mapGetters('widget', ['isMobileViewport']),
    SizeRecommendationStep() {
      return SizeRecommendationStep;
    },
    showBackButton() {
      return (
        this.isMobileViewport && this.step !== SizeRecommendationStep.INPUT
      );
    },
    sizeFeedbackFilterParams() {
      return Object.entries(this.selectedSizeFeedbackFilters)
        .filter(([, values]) => values.length > 0)
        .reduce(
          (params, [filterId, values]) => ({
            ...params,
            [filterId]: values.join(',')
          }),
          {}
        );
    }
  },
  mounted() {
    this.$emit('ready');
  },
  methods: {
    onClose() {
      this.windowService.closeFullscreenPopup();
    },
    onBack() {
      this.backCount += 1;
      this.step = SizeRecommendationStep.INPUT;
    },
    async onSubmit({ height, weight, preferredFitType }) {
      this.step = SizeRecommendationStep.LOADING;
      this.height = height;
      this.weight = weight;
      this.preferredFitType = preferredFitType;
      const orgBackCount = this.backCount;

      const sizeRecommendationRequest = this.api.fetchSizeRecommendation({
        product_code: this.productCode,
        height,
        weight,
        fit_type: preferredFitType
      });
      const sizeFeedbackRequest = this.api.fetchSizeFeedbacks({
        product_code: this.productCode
      });
      const sizeFeedbackFiltersRequest = this.api.fetchSizeFeedbackFilters(
        this.productCode
      );

      const [
        { data: sizeRecommendationData },
        { data: sizeFeedbackData },
        { data: sizeFeedbackFiltersData }
      ] = await Promise.all([
        sizeRecommendationRequest,
        sizeFeedbackRequest,
        sizeFeedbackFiltersRequest,
        delay(MINIMUM_LOADING_DURATION)
      ]);
      if (this.backCount !== orgBackCount) return;

      this.recommendedSizes = sizeRecommendationData.recommended_sizes;
      this.sizeOptionStats = sizeRecommendationData.size_option_stats;
      this.sizeFeedbacks = sizeFeedbackData.feedbacks;
      this.sizeFeedbacksPagy = sizeFeedbackData.pagy;
      this.sizeFeedbackFilters = sizeFeedbackFiltersData;
      this.selectedSizeFeedbackFilters = {};
      await this.applyRecommendedSizeFilter(orgBackCount);
      if (this.backCount !== orgBackCount) return;
      this.step = SizeRecommendationStep.RESULT;
    },
    async applyRecommendedSizeFilter(orgBackCount) {
      const purchasedSizeFilter = this.sizeFeedbackFilters
        ?.purchased_size_filter;
      const mainRecommendedSize = this.recommendedSizes?.[0];
      if (!purchasedSizeFilter || !mainRecommendedSize) return;

      const option = purchasedSizeFilter.options.find(
        ({ name }) => name === mainRecommendedSize
      );
      if (!option) return;

      this.selectedSizeFeedbackFilters = {
        [purchasedSizeFilter.id]: [option.value]
      };
      const { data } = await this.api.fetchSizeFeedbacks({
        product_code: this.productCode,
        filters: this.sizeFeedbackFilterParams
      });
      if (this.backCount !== orgBackCount) return;

      if (data.feedbacks.length > 0) {
        this.sizeFeedbacks = data.feedbacks;
        this.sizeFeedbacksPagy = data.pagy;
      } else {
        this.selectedSizeFeedbackFilters = {};
      }
    },
    async onLoadMoreSizeFeedbacks() {
      if (this.isLoadingSizeFeedbacks) return;

      this.isLoadingSizeFeedbacks = true;
      const orgBackCount = this.backCount;
      const { data } = await this.api.fetchSizeFeedbacks({
        product_code: this.productCode,
        page: this.sizeFeedbacksPagy.next,
        filters: this.sizeFeedbackFilterParams
      });
      this.isLoadingSizeFeedbacks = false;
      if (this.backCount !== orgBackCount) return;

      this.sizeFeedbacks = [...this.sizeFeedbacks, ...data.feedbacks];
      this.sizeFeedbacksPagy = data.pagy;
    },
    onChangeSizeFeedbackFilter(filterId, values) {
      this.selectedSizeFeedbackFilters = {
        ...this.selectedSizeFeedbackFilters,
        [filterId]: values
      };
      this.refetchSizeFeedbacks();
    },
    async refetchSizeFeedbacks() {
      const orgBackCount = this.backCount;
      const orgSelectedFilters = this.selectedSizeFeedbackFilters;

      this.isLoadingSizeFeedbacks = true;
      const { data } = await this.api.fetchSizeFeedbacks({
        product_code: this.productCode,
        filters: this.sizeFeedbackFilterParams
      });
      this.isLoadingSizeFeedbacks = false;
      if (this.backCount !== orgBackCount) return;
      if (this.selectedSizeFeedbackFilters !== orgSelectedFilters) return;

      this.sizeFeedbacks = data.feedbacks;
      this.sizeFeedbacksPagy = data.pagy;
    }
  }
};
